import isNil from "lodash/isNil";
import { Inline } from "PFComponents/containers/flex/inline";
import { Icon } from "PFComponents/icon";
import { Tooltip } from "PFComponents/tooltip";
import { Typography } from "PFComponents/typography/typography";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import css from "../match_gauge.module.scss";

type AvailabilityIconProps = {
  availabilityScore: number;
};

const AvailabilityIcon = ({ availabilityScore }: AvailabilityIconProps) => {
  const { t } = useTranslation("core", { keyPrefix: "matchRating" });

  const ratingsArray = useMemo(() => [t("weak"), t("decent"), t("good"), t("best")], [t]);

  const level = availabilityScore >= 1 ? 4 : availabilityScore <= 0 ? 0 : Math.floor(availabilityScore * 4);
  const availabilityTipContent = `${ratingsArray[level - 1] || t("noMatch")} ${t("description")}`;

  return (
    <Tooltip content={availabilityTipContent} triggerContainerProps={{ className: css.infoIcon }}>
      <Icon name="info" size="sm" />
    </Tooltip>
  );
};

type AvailabilityInfoProps = {
  availabilityScore: number | null | undefined;
  icon?: React.ReactElement;
};

export const AvailabilityInfo = ({ availabilityScore, icon }: AvailabilityInfoProps) => {
  const { t } = useTranslation("core");

  if (isNil(availabilityScore)) {
    return null;
  }

  const availabilityPercent = (Math.min(availabilityScore, 1) * 100).toFixed(1);
  const availabilityIcon = icon || <AvailabilityIcon availabilityScore={availabilityScore} />;

  return (
    <Inline className={css.availability} alignItems="center" gap="spacingSm">
      {availabilityIcon}
      <Typography variant="labelSmallRegular" tag="span" clipOverflow>
        <strong>{`${availabilityPercent}% `}</strong>
        {t("availability")}
      </Typography>
    </Inline>
  );
};
